/**
 * Ring builders over the room model: the canonical alcove ring (THE single
 * definition of alcove geometry - union, editor line-frames, and perimeter
 * walk all derive from it) and the chamfered rectangle ring.
 */
import { alcoveDepth, AlcoveEdgeName, EdgeName, FloorPlanCorner, FloorPlanEdgeSegment, FloorPlanRoom, OuterWhich, Point, SQUARE_CORNER } from '../schemas';
import { alongLeg, edgeFrame, perpLeg, worldOnLine } from './frames';
import { samePoint } from './pointLib';

/**
 * The 6 raw vertices of an outward alcove's ring, in boundary-walk order
 * mouth-left → left wall → back wall → right wall → mouth-right. The implicit
 * closing edge (last → first) is the mouth shared with the main room. Only the
 * two OUTER (back) corners carry a chamfer, cutting inward at the tip and
 * removing area; the mouth is exactly the span [position, position+width] (there
 * are no inner/front corners - a flared mouth is modelled by abutting alcoves).
 *
 * Vertex indices are stable:
 *   0 mouth-left · 1 left-high · 2 tip-near · 3 tip-far · 4 right-high · 5 mouth-right
 */
export function alcoveRingVerts(shape: FloorPlanRoom, edge: EdgeName, a: FloorPlanEdgeSegment): Point[] {
  const fr = edgeFrame(edge, shape.width, shape.length);
  const { position: p, width: wd } = a;
  const d = alcoveDepth(a);
  const blc = a.backLeftCorner ?? { dx: 0, dy: 0 };
  const brc = a.backRightCorner ?? { dx: 0, dy: 0 };
  const A = (c: FloorPlanCorner) => alongLeg(c, fr.horizontal);
  const Q = (c: FloorPlanCorner) => perpLeg(c, fr.horizontal);
  const w = (along: number, out: number) => worldOnLine(fr, along, out);
  return [
    w(p, 0), // 0 mouth-left (left wall foot)
    w(p, d - Q(blc)), // 1 left wall, high
    w(p + A(blc), d), // 2 tip near
    w(p + wd - A(brc), d), // 3 tip far
    w(p + wd, d - Q(brc)), // 4 right wall, high
    w(p + wd, 0), // 5 mouth-right (right wall foot)
  ];
}

/** The source-geometry role of one alcove ring sub-edge. */
export type AlcoveEdgeRole =
  | { kind: 'wall'; wall: AlcoveEdgeName }
  | { kind: 'corner'; which: OuterWhich };

/** A labelled sub-edge of an alcove ring (mouth excluded). */
export interface AlcoveRingEdge {
  a: Point;
  b: Point;
  role: AlcoveEdgeRole;
}

// Which consecutive ring vertices form each labelled sub-edge (indices per alcoveRingVerts).
const ALCOVE_RING_TOPOLOGY: { from: number; to: number; role: AlcoveEdgeRole }[] = [
  { from: 0, to: 1, role: { kind: 'wall', wall: 'leftEdge' } },
  { from: 1, to: 2, role: { kind: 'corner', which: 'backLeftCorner' } },
  { from: 2, to: 3, role: { kind: 'wall', wall: 'backEdge' } },
  { from: 3, to: 4, role: { kind: 'corner', which: 'backRightCorner' } },
  { from: 4, to: 5, role: { kind: 'wall', wall: 'rightEdge' } },
];

/**
 * The alcove's perimeter as labelled sub-edges (mouth excluded), with degenerate
 * (zero-length) edges dropped. THE single source of the ring's wall/corner
 * topology - consumed by the perimeter walk and the editor wall line-frames.
 */
export function alcoveRingEdges(shape: FloorPlanRoom, edge: EdgeName, a: FloorPlanEdgeSegment): AlcoveRingEdge[] {
  const v = alcoveRingVerts(shape, edge, a);
  return ALCOVE_RING_TOPOLOGY
    .filter(e => !samePoint(v[e.from], v[e.to]))
    .map(e => ({ a: v[e.from], b: v[e.to], role: e.role }));
}

/** A rectangle [x,x+w]×[y,y+h] with inward-carving corner chamfers, CW.
 *  A missing corner is square. */
export function rectRing(
  x: number,
  y: number,
  w: number,
  h: number,
  corners: {
    backLeftCorner?: FloorPlanCorner;
    backRightCorner?: FloorPlanCorner;
    frontRightCorner?: FloorPlanCorner;
    frontLeftCorner?: FloorPlanCorner;
  },
): Point[] {
  const {
    backLeftCorner: tl = SQUARE_CORNER,
    backRightCorner: tr = SQUARE_CORNER,
    frontRightCorner: br = SQUARE_CORNER,
    frontLeftCorner: bl = SQUARE_CORNER,
  } = corners;
  const pts: Point[] = [
    { x: x + tl.dx, y },
    { x: x + w - tr.dx, y },
    { x: x + w, y: y + tr.dy },
    { x: x + w, y: y + h - br.dy },
    { x: x + w - br.dx, y: y + h },
    { x: x + bl.dx, y: y + h },
    { x, y: y + h - bl.dy },
    { x, y: y + tl.dy },
  ];
  // drop coincident vertices (zero-leg corners)
  const clean: Point[] = [];
  for (const p of pts) {
    if (clean.length === 0 || !samePoint(clean[clean.length - 1], p)) clean.push(p);
  }
  while (clean.length > 1 && samePoint(clean[clean.length - 1], clean[0])) clean.pop();
  return clean;
}
